/* eslint-disable no-unused-vars */
import { withStyles } from '@material-ui/core';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  Redirect,
  Route,
  Switch,
  withRouter,
} from 'react-router-dom';
import { ZendeskAPI } from 'react-zendesk';
import PF2MAlertDialog from '~/components/PF2MAlertDialog';
import PF2MMenu from '~/components/PF2MMenu';
import PF2MScrollbar from '~/components/PF2MScrollbar';
import { useFeatureFlagPlatformConfiguration } from '~/services/platformConfiguration';
import useTabletConfiguration from '~/services/tabletConfiguration';
import { getTabletConfiguration, setAlertDialog } from '~/store/manager/actions';
import ArrowForwardIosIcon from '@material-ui/icons/ArrowForwardIos';
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos';
import Main from '../Main';
import DispatchControl from './DispatchControl';
import EquipmentPrograms from './EquipmentPrograms';
import EquipmentStatus from './EquipmentStatus';
import EquipmentStatusV3 from './EquipmentStatusV3';
import LoadSchedules from './LoadSchedules';
import OperationalControl from './OperationalControl';
import PerforatorSchedules from './PerforatorSchedules';
import styles from './styles';
import Terrain from './Terrain';
import { getDispatchCodeIcons, setPermissionAlert } from '~/store/dispatch/actions';
import PF2MValidationModal from '~/components/PF2MValidationModal';
import Chat from './Chat';
import Anomaly from './Anomaly';
import PrivateRoute from '~/routeGuard/privateRoute';

const Dispatch = ({ classes, match, history }) => {
  const dispatch = useDispatch();
  const alertDialog = useSelector(state => state.manager.alertDialog);
  const permissionAlert = useSelector(state => state.dispatch.permissionAlert);
  // eslint-disable-next-line max-len
  const isEquipmentsSchedulesDirty = useSelector(state => state.dispatch.isEquipmentsSchedulesDirty);
  // eslint-disable-next-line no-underscore-dangle
  const roles = useSelector(state => state.auth.user._roles);
  const { t: translate } = useTranslation();
  const showDispatchOperationalControl = useFeatureFlagPlatformConfiguration(
    'dispatch_operational_control_tab', 0,
  );

  const [isMenuOpen, setIsMenuOpen] = React.useState(true);
  const menuWidth = isMenuOpen ? 130 : 0;

  const handleMenuContainer = () => {
    setIsMenuOpen(prev => !prev);
  };

  const hasDispatchPermission = userRoles => Array.isArray(userRoles) && userRoles.some(role => role?.module_value === 'DESPACHO');

  const dispatchPermission = hasDispatchPermission(roles);

  const validateNavigation = useCallback(({ pathname = '' }) => {
    if ((isEquipmentsSchedulesDirty) && !pathname.includes('equipmentprogram')) {
      dispatch(setAlertDialog());
      return false;
    }
    return true;
  }, [isEquipmentsSchedulesDirty, dispatch]);

  const handleNavigation = (e, pathname = '') => {
    if (!validateNavigation(pathname)) {
      e.preventDefault();
    }
  };

  useEffect(() => {
    if (history.location && history.location.length > 0) {
      validateNavigation(history.location);
    }
  }, [history.location, validateNavigation]);

  useEffect(() => {
    const timer = setTimeout(() => {
      ZendeskAPI('webWidget', 'hide');
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    dispatch(getTabletConfiguration());
    dispatch(getDispatchCodeIcons());
  }, []);


  const { value: dispatchRealTime } = useTabletConfiguration('dispatch_real_time');
  const { value: dispatchUpdateTime } = useTabletConfiguration('dispatch_update_time');

  useEffect(() => {
    if (dispatchRealTime === undefined || dispatchUpdateTime === undefined) {
      dispatch(getTabletConfiguration());
    }
  }, [dispatchUpdateTime]);

  const isRealTimeEnabled = dispatchRealTime !== undefined && String(dispatchRealTime) === '1';

  const excludeTabs = [
    !showDispatchOperationalControl && 'operationalcontrol',
    !isRealTimeEnabled && 'v2/equipmentstatus',
  ].filter(Boolean);

  return (
    <DndProvider backend={HTML5Backend}>
      <Main hasLoading={!isRealTimeEnabled}>
        <div className={classes.container}>
          <div style={{ display: 'flex' }}>
            <div
              style={{
                display: 'flex',
                width: menuWidth,
                flex: `0 0 ${menuWidth}px`,
                height: '100%',
                overflowY: 'hidden',
                transition: 'width 0.5s ease',
                willChange: 'width',
              }}
            >
              <PF2MScrollbar>
                <PF2MMenu
                  handleNavigation={handleNavigation}
                  moduleName="dispatch"
                  excludePaths={excludeTabs}
                  roles={roles}
                  dispatchPermission={dispatchPermission}
                />
              </PF2MScrollbar>
            </div>
            <div>
              <div>
                {isMenuOpen && (
                  <ArrowBackIosIcon
                    style={{
                      cursor: 'pointer',
                      marginTop: 72,
                      padding: 4,
                      transition: 'transform 300ms ease',
                      borderRadius: '4px 0px 0px 4px',
                      backgroundColor: 'white',
                    }}
                    onClick={handleMenuContainer}
                  />
                )}
                {!isMenuOpen && (
                  <ArrowForwardIosIcon
                    style={{
                      cursor: 'pointer',
                      marginTop: 72,
                      padding: 4,
                      transition: 'transform 300ms ease',
                      borderRadius: '4px 0px 0px 4px',
                      backgroundColor: 'white',
                    }}
                    onClick={handleMenuContainer}
                  />
                )}
              </div>
            </div>
          </div>
          <div
            style={{
              flex: 1,
              minWidth: 0,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                flex: 1,
                minWidth: 0,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                overflowX: 'auto',
                overflowY: 'auto',
              }}
            >
              <Switch>
                <PrivateRoute
                  exact
                  path={`${match.path}/equipmentprogram`}
                  component={EquipmentPrograms}
                  userAccessLevel={[1, 2, 3]}
                  requiredModule="DispatchModule"
                />
                <Route exact path={`${match.path}/equipmentstatus`} component={EquipmentStatus} />
                <PrivateRoute
                  exact
                  path={`${match.path}/v2/equipmentstatus`}
                  component={EquipmentStatusV3}
                  userAccessLevel={[1, 2, 3]}
                  requiredModule="DispatchModule"
                />
                <PrivateRoute
                  exact
                  path={`${match.path}/loadschedules`}
                  component={LoadSchedules}
                  userAccessLevel={[1, 2, 3]}
                  requiredModule="DispatchModule"
                />
                <PrivateRoute
                  exact
                  path={`${match.path}/perforatorschedules`}
                  component={PerforatorSchedules}
                  userAccessLevel={[1, 2, 3]}
                  requiredModule="DispatchModule"
                />
                <PrivateRoute
                  exact
                  path={`${match.path}/dispatchcontrol`}
                  component={DispatchControl}
                  userAccessLevel={[1, 2, 3]}
                  requiredModule="DispatchModule"
                />
                <PrivateRoute
                  exact
                  path={`${match.path}/operationalcontrol`}
                  component={OperationalControl}
                  userAccessLevel={[1, 2, 3]}
                  requiredModule="DispatchModule"
                />
                <PrivateRoute
                  exact
                  path={`${match.path}/terrain`}
                  component={Terrain}
                  userAccessLevel={[1, 2, 3]}
                  requiredModule="DispatchModule"
                />
                <PrivateRoute
                  exact
                  path={`${match.path}/chat`}
                  component={Chat}
                  userAccessLevel={[1, 2, 3]}
                  requiredModule="DispatchModule"
                />
                <PrivateRoute
                  exact
                  path={`${match.path}/anomaly`}
                  component={Anomaly}
                  userAccessLevel={[1, 2, 3]}
                  requiredModule="DispatchModule"
                />
                {dispatchPermission
                  ? <Redirect from={`${match.path}`} to={`${match.path}/equipmentprogram`} />
                  : <Redirect from={`${match.path}`} to={`${match.path}/equipmentstatus`} />
                }
              </Switch>
            </div>
          </div>
          <PF2MAlertDialog
            hasCancel={false}
            confirmText={translate('common:Understood')}
            description={translate('validation:YouHaveUnsavedData')}
            open={alertDialog}
            onConfirm={() => dispatch(setAlertDialog())}
          />
          <PF2MValidationModal
            description={translate('validation:ValidationDispachModule')}
            modalData={permissionAlert}
            closeModal={() => dispatch(setPermissionAlert(null))}
          />
        </div>
      </Main>
    </DndProvider>
  );
};

Dispatch.propTypes = {
  classes: PropTypes.object.isRequired,
  match: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
};

Dispatch.defaultProps = {};

export default withRouter(withStyles(styles)(Dispatch));
